import { makeStyles } from '@material-ui/core/styles';
import { wppColors } from '../../../App.styles';

// MUI v4 `breakpoints.down()` only resolves the built-in xs..xl keys, so the
// custom `medium` breakpoint has to be passed as a number.
const MOBILE_MAX = 768;

export const useStyles = makeStyles((theme) => ({
  NavMenu: {
    position: 'relative',
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    boxSizing: 'border-box',
    overflowX: 'auto',
    backgroundColor: wppColors.white,
    borderBottom: `1px solid ${theme.palette.grey3}`,
    padding: '0 16px',
    zIndex: 2,

    [theme.breakpoints.down(MOBILE_MAX)]: {
      display: 'none',
      flexDirection: 'column',
      overflowX: 'visible',
      padding: 0,
      boxShadow: '0 12px 24px rgba(0, 0, 80, 0.12)',

      '&.open': {
        display: 'flex',
      },
    },

    [theme.breakpoints.up('extraLarge')]: {
      padding: '0 24px',
    },
  },

  hideScreen: {
    display: 'none',
  },

  navItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: '4px',
    listStyle: 'none',
    margin: 0,
    padding: 0,

    [theme.breakpoints.down(MOBILE_MAX)]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 0,
      width: '100%',
      padding: '4px 0 8px',
    },
  },

  /**
   * "You still owe this" marker, used by the Skill Matrix entry while the
   * person has not been through the skills form.
   *
   * A hollow ring rather than a filled badge with a count: there is exactly
   * one thing pending and no number worth showing, and a filled dot reads as
   * an unread notification — something new arrived — when what this means is
   * something of yours is unfinished.
   */
  pendingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    border: `2px solid var(--app-color, ${wppColors.navy})`,
    flexShrink: 0,
    marginLeft: '2px',
  },

  navButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
    height: '48px',
    padding: '0 16px',
    cursor: 'pointer',
    color: wppColors.black,
    borderBottom: '2px solid transparent',
    borderRadius: '6px 6px 0 0',
    transition: 'color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease',
    whiteSpace: 'nowrap',

    '&:hover': {
      backgroundColor: '#F2F4F7',
      color: `var(--app-color, ${wppColors.navy})`,
    },

    '&.active': {
      color: `var(--app-color, ${wppColors.navy})`,
      borderBottomColor: `var(--app-color, ${wppColors.navy})`,
    },

    '&.active:hover': {
      backgroundColor: '#F2F4F7',
      borderBottomColor: `var(--app-color, ${wppColors.navy})`,
    },

    '&:focus-visible': {
      outline: `2px solid var(--app-color, ${wppColors.navy})`,
      outlineOffset: '-2px',
    },

    [theme.breakpoints.down(MOBILE_MAX)]: {
      height: '46px',
      padding: '0 16px',
      borderRadius: 0,
      borderBottom: 0,
      whiteSpace: 'normal',

      // A bottom underline reads poorly on a full-width row, so use a left marker
      '&.active': {
        backgroundColor: '#F2F4F7',
        boxShadow: `inset 3px 0 0 var(--app-color, ${wppColors.navy})`,
      },

      '&.active:hover': {
        backgroundColor: '#ECEFF3',
        boxShadow: `inset 3px 0 0 var(--app-color, ${wppColors.navy})`,
      },
    },
  },

  // First entry of a new app. A hairline rather than a full-height rule: the
  // border version boxed every item and the bar read as table cells. This one
  // is inset well clear of the top and bottom edges, so it separates without
  // drawing a cell around anything.
  groupStart: {
    position: 'relative',
    marginLeft: '15px',

    '&::before': {
      content: '""',
      position: 'absolute',
      left: '-8px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '1px',
      height: '16px',
      backgroundColor: theme.palette.grey3,
    },

    [theme.breakpoints.down(MOBILE_MAX)]: {
      marginLeft: 0,
      marginTop: '7px',

      // Horizontal in the stacked menu, inset from both sides for the same
      // reason: it marks a break, it does not frame a row.
      '&::before': {
        left: '16px',
        right: '16px',
        top: '-4px',
        transform: 'none',
        width: 'auto',
        height: '1px',
      },
    },
  },

  // --- Deskbooking: one entry, two screens -------------------------------

  // Desktop shows the menu trigger, the stacked mobile menu shows the group.
  deskDesktop: {
    display: 'flex',

    [theme.breakpoints.down(MOBILE_MAX)]: {
      display: 'none',
    },
  },

  deskMobile: {
    display: 'none',

    [theme.breakpoints.down(MOBILE_MAX)]: {
      display: 'block',
    },
  },

  // Resets the <button> so it renders exactly like the <li> items beside it.
  menuTrigger: {
    background: 'transparent',
    border: 0,
    borderBottom: '2px solid transparent',
    font: 'inherit',
    margin: 0,
  },

  chevron: {
    width: '16px',
    height: '16px',
    flexShrink: 0,
    marginLeft: '-2px',
    opacity: 0.6,
    transition: 'transform 0.2s ease, opacity 0.15s ease',

    '&.open': {
      transform: 'rotate(180deg)',
      opacity: 1,
    },
  },

  menuPaper: {
    marginTop: '6px',
    minWidth: '300px',
    borderRadius: '12px',
    border: `1px solid ${theme.palette.grey3}`,
    boxShadow: '0 12px 32px rgba(0, 0, 80, 0.14), 0 2px 6px rgba(0, 0, 80, 0.06)',
  },

  menuList: {
    padding: '6px',
  },

  menuItem: {
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '8px',
    whiteSpace: 'normal',

    '&:hover': {
      backgroundColor: '#F2F4F7',
    },

    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: '#EEF0F8',
    },

    '& + &': {
      marginTop: '2px',
    },
  },

  menuIconTile: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    flexShrink: 0,
    borderRadius: '8px',
    backgroundColor: '#EEF0F8',
    color: wppColors.navy,
    transition: 'background-color 0.15s ease, color 0.15s ease',

    '& svg': {
      fill: 'none',
    },

    // The screen you are on gets the solid tile, so the menu answers
    // "where am I" before you read a word.
    '.Mui-selected &': {
      backgroundColor: wppColors.navy,
      color: wppColors.white,
    },
  },

  menuText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },

  menuTitle: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    color: wppColors.black,

    '.Mui-selected &': {
      color: wppColors.navy,
    },
  },

  menuHint: {
    fontSize: '12.5px',
    lineHeight: '17px',
    color: theme.palette.grey2,
  },

  groupLabel: {
    margin: 0,
    padding: '12px 16px 4px',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: theme.palette.grey1,
  },

  subList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },

  logoutItem: {
    display: 'none',

    [theme.breakpoints.down(MOBILE_MAX)]: {
      display: 'flex',
      marginTop: '4px',
      paddingTop: '4px',
      borderTop: `1px solid ${theme.palette.grey3}`,
    },
  },

  toggleNavMenuButton: {
    display: 'none',
  },

  buttonIcon: {
    fill: wppColors.white,
    stroke: 'currentColor',
    width: '20px',
    height: '20px',
    minWidth: '20px',
    minHeight: '20px',
    flexShrink: 0,
    transition: 'stroke 0.15s ease',
  },

  buttonText: {
    margin: 0,
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '100%',
    letterSpacing: '0.1px',
    color: 'inherit',
    whiteSpace: 'nowrap',
    transition: 'color 0.15s ease',

    '&.active': {
      fontWeight: 700,
    },
  },
}));